'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { reloadPage } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { ConversationActionProps } from '@marketplace-web/messaging/conversation-action-data'
import { ConversationActionButton } from '@marketplace-web/messaging/conversation-action-feature'
import {
  RefundOfferAction,
  handleRefundOffer,
  getRefundOfferId,
  transformRefundOfferIdDto,
  partialRefundOffersClickEvent,
  transformEscrowRefundEstimateResp,
  getRefundOffersEstimate,
  RefundElements,
} from '@marketplace-web/complaints/complaints-data'

import GenericErrorModal from '../GenericErrorModal'
import PartialRefundOfferBuyerProtectionFeeDialog from '../PartialRefundOfferBuyerProtectionFeeDialog'
import AcceptPartialRefundOfferDialog from './AcceptPartialRefundOfferDialog'
import PartialRefundOfferCurrencyConversionDialog from '../PartialRefundOfferCurrencyConversionDialog'
import { findRefundEstimateElement } from '../../utils/general'

enum AcceptPartialRefundOfferActiveDialog {
  AcceptPartialRefundDialog,
  BuyerProtectionFeeDialog,
  CurrencyConversionDialog,
  ErrorDialog,
  None,
}

const AcceptPartialRefundOffer = ({
  primary,
  title,
  inverse,
  theme,
  size,
}: ConversationActionProps) => {
  const { track } = useTracking()

  const { transaction } = useConversationContext()

  const transactionId = transaction?.id?.toString()
  const complaintId = transaction?.complaintId

  const [shouldFetchData, setShouldFetchData] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [activeDialog, setActiveDialog] = useState(AcceptPartialRefundOfferActiveDialog.None)

  const {
    fetch: fetchRefundOfferId,
    transformedData: refundOfferId,
    isLoading: isRefundOfferIdLoading,
  } = useFetch(getRefundOfferId, response => transformRefundOfferIdDto(response)?.refundOfferId)

  const {
    fetch: fetchRefundEstimate,
    transformedData: refundEstimate,
    isLoading: isRefundEstimateLoading,
  } = useFetch(getRefundOffersEstimate, transformEscrowRefundEstimateResp)

  const refundOfferElement = useMemo(
    () => findRefundEstimateElement(refundEstimate?.refundElements, RefundElements.RefundOffer),
    [refundEstimate],
  )

  const fetchData = useCallback(async () => {
    if (
      activeDialog !== AcceptPartialRefundOfferActiveDialog.AcceptPartialRefundDialog ||
      !complaintId ||
      !shouldFetchData
    )
      return

    const { error, transformedData } = await fetchRefundOfferId(Number(complaintId))

    if (error || !transformedData) {
      setActiveDialog(AcceptPartialRefundOfferActiveDialog.ErrorDialog)

      return
    }

    const response = await fetchRefundEstimate(transformedData)

    if (response.error || !response.transformedData)
      setActiveDialog(AcceptPartialRefundOfferActiveDialog.ErrorDialog)

    setShouldFetchData(false)
  }, [activeDialog, complaintId, fetchRefundEstimate, fetchRefundOfferId, shouldFetchData])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const openDialog = (dialogType: AcceptPartialRefundOfferActiveDialog) => {
    setActiveDialog(dialogType)
  }

  const closeDialog = (dialogType = AcceptPartialRefundOfferActiveDialog.None) => {
    setActiveDialog(dialogType)
    setShouldFetchData(true)
  }

  const handleErrorDialogClose = () => {
    closeDialog()
    reloadPage()
  }

  const trackAcceptOfferClick = () => {
    if (!transactionId || !complaintId) return

    const targetDetails = JSON.stringify({
      partial_refund_amount: Number(refundOfferElement?.money.amount),
      refund_offer_id: refundOfferId?.toString(),
    })

    track(
      partialRefundOffersClickEvent({
        target: 'partial_refund_offer_accept',
        transactionId,
        complaintId,
        targetDetails,
      }),
    )
  }

  const acceptPartialRefundOffer = async () => {
    if (isSubmitting || !refundOfferId) return

    setIsSubmitting(true)

    const response = await handleRefundOffer({
      refundOfferId,
      action: RefundOfferAction.Accept,
    })

    trackAcceptOfferClick()
    setIsSubmitting(false)

    if ('errors' in response) {
      openDialog(AcceptPartialRefundOfferActiveDialog.ErrorDialog)
    } else {
      closeDialog()
    }
  }

  const renderCurrencyConversionDialogue = () => {
    if (!refundEstimate) return null

    return (
      <PartialRefundOfferCurrencyConversionDialog
        isOpenedInDialog
        refundEstimate={refundEstimate}
        isOpen={activeDialog === AcceptPartialRefundOfferActiveDialog.CurrencyConversionDialog}
        onClose={() => openDialog(AcceptPartialRefundOfferActiveDialog.AcceptPartialRefundDialog)}
      />
    )
  }

  return (
    <>
      <ConversationActionButton
        text={title}
        primary={primary}
        size={size}
        theme={theme}
        inverse={inverse}
        onClick={() => openDialog(AcceptPartialRefundOfferActiveDialog.AcceptPartialRefundDialog)}
        testId="accept-partial-refund-offer-conversation-action"
      />
      <AcceptPartialRefundOfferDialog
        refundEstimate={refundEstimate}
        isRefundEstimateLoading={isRefundEstimateLoading || isRefundOfferIdLoading}
        isOpen={activeDialog === AcceptPartialRefundOfferActiveDialog.AcceptPartialRefundDialog}
        onCurrencyConversionClick={() =>
          openDialog(AcceptPartialRefundOfferActiveDialog.CurrencyConversionDialog)
        }
        onBuyerProtectionFeeInfoClick={() =>
          openDialog(AcceptPartialRefundOfferActiveDialog.BuyerProtectionFeeDialog)
        }
        onSubmit={acceptPartialRefundOffer}
        onClose={closeDialog}
      />
      <PartialRefundOfferBuyerProtectionFeeDialog
        isOpenedInDialog
        isOpen={activeDialog === AcceptPartialRefundOfferActiveDialog.BuyerProtectionFeeDialog}
        onClose={() => openDialog(AcceptPartialRefundOfferActiveDialog.AcceptPartialRefundDialog)}
      />
      {renderCurrencyConversionDialogue()}
      <GenericErrorModal
        isShown={activeDialog === AcceptPartialRefundOfferActiveDialog.ErrorDialog}
        onClose={handleErrorDialogClose}
      />
    </>
  )
}

export default AcceptPartialRefundOffer
